import { useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { formatEther } from "viem";
import { brain721CA, BRAIN_NFT_ABI, useBatchManagement } from "./balances";
import SvgSpinner from "./svgspinner";
import { useEffect, useState } from "react";

export const CollectiveMintingStatus = ({
  creditsPooledIsLoading,
  totalPooledCredits,
}) => {
  const [batchPhases, setBatchPhases] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [mintingBatchId, setMintingBatchId] = useState(null);
  const [claimingBatchId, setClaimingBatchId] = useState(null);

  const {
    batchStatuses,
    readyToMintBatches,
    readyToClaimBatches,
    totalUnclaimedTokens,
    loading,
    error,
  } = useBatchManagement();

  const {
    data: mintHash,
    error: mintError,
    isPending: isMintPending,
    writeContract: writeMintContract,
  } = useWriteContract();

  const {
    data: claimHash,
    error: claimError,
    isPending: isClaimPending,
    writeContract: writeClaimContract,
  } = useWriteContract();

  const { isLoading: isMintConfirming, isSuccess: isMintConfirmed } =
    useWaitForTransactionReceipt({
      hash: mintHash,
    });

  const { isLoading: isClaimConfirming, isSuccess: isClaimConfirmed } =
    useWaitForTransactionReceipt({
      hash: claimHash,
    });

  useEffect(() => {
    if (isMintConfirmed && mintingBatchId) {
      console.log("Mint transaction confirmed for batch:", mintingBatchId);
      setBatchPhases((prev) => ({
        ...prev,
        [mintingBatchId]: "claim-phase",
      }));
      setLoadingStates((prev) => ({
        ...prev,
        [mintingBatchId]: false,
      }));
      setMintingBatchId(null);
    }
  }, [isMintConfirmed, mintingBatchId]);

  // Effect for handling claim confirmation
  useEffect(() => {
    if (isClaimConfirmed && claimingBatchId) {
      console.log("Claim transaction confirmed for batch:", claimingBatchId);
      setBatchPhases((prev) => ({
        ...prev,
        [claimingBatchId]: "claimed",
      }));
      setLoadingStates((prev) => ({
        ...prev,
        [claimingBatchId]: false,
      }));
      setClaimingBatchId(null);
    }
  }, [isClaimConfirmed, claimingBatchId]);

  const handleCollectiveMint = async (batchId) => {
    setMintingBatchId(batchId);
    setLoadingStates((prev) => ({
      ...prev,
      [batchId]: true,
    }));

    try {
      await writeMintContract({
        address: brain721CA,
        abi: BRAIN_NFT_ABI,
        functionName: "collectiveMint",
        args: [batchId],
      });
    } catch (error) {
      console.error("Failed to send mint transaction:", error);
      setLoadingStates((prev) => ({
        ...prev,
        [batchId]: false,
      }));
      setMintingBatchId(null);
    }
  };

  const handleClaimTokens = async (batchId) => {
    setClaimingBatchId(batchId);
    setLoadingStates((prev) => ({
      ...prev,
      [batchId]: true,
    }));

    try {
      await writeClaimContract({
        address: brain721CA,
        abi: BRAIN_NFT_ABI,
        functionName: "claimTokens",
        args: [batchId],
      });
    } catch (error) {
      console.error("Failed to send claim transaction:", error);
      setLoadingStates((prev) => ({
        ...prev,
        [batchId]: false,
      }));
      setClaimingBatchId(null);
    }
  };

  if (loading) {
    return <SvgSpinner />;
  }

  if (error) {
    return <div>Error loading batch data: {error.message}</div>;
  }

  if (!batchStatuses || batchStatuses.length === 0) {
    return (
      <small className="font-mono text-uppercase d-block d-flex align-items-center">
        <span
          className="muted d-flex align-items-center"
          style={{ color: "cyan" }}
        >
          No pooled credits found.
        </span>
      </small>
    );
  }

  return (
    <>
      <small className="font-mono text-uppercase d-block d-flex align-items-center mb-4">
        <span className=" d-flex align-items-center">
          {loading || creditsPooledIsLoading ? (
            <SvgSpinner />
          ) : (
            `You have ${formatEther(
              totalPooledCredits
            )} credits pooled for collective minting. Once enough users have pooled credits with you, a new Brain will be minted and you will receive a share of that Brain's tokens that correlate to your share of the pool.`
          )}
        </span>
      </small>
      <small className="font-mono text-uppercase d-block d-flex align-items-center mb-4">
        Your Batch Status:
      </small>

      {readyToMintBatches.length > 0 && (
        <small
          className="font-mono text-uppercase d-block d-flex align-items-center mb-4"
          style={{ color: "cyan" }}
        >
          You have {readyToMintBatches.length} batch(es) ready to mint!
        </small>
      )}

      {readyToClaimBatches.length > 0 && (
        <small
          className="font-mono text-uppercase d-block d-flex align-items-center mb-4"
          style={{ color: "cyan" }}
        >
          You have {formatEther(totalUnclaimedTokens)} brain tokens ready to
          claim!
        </small>
      )}
      <div className="row">
        {batchStatuses.map((batch) => (
          <div key={batch.batchId} className="mb-4">
            <div
              className="p-3 h-100"
              style={{
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "24px",
              }}
            >
              <small className="font-mono text-uppercase d-block d-flex align-items-center mb-2">
                Batch #{batch.batchId}
              </small>
              <small className="font-mono text-uppercase d-block d-flex align-items-center mb-2">
                Your Contribution:{" "}
                <span className="muted">
                  {formatEther(batch.contribution)} credits
                </span>
              </small>
              <small className="font-mono text-uppercase d-block d-flex align-items-center mb-2">
                Total Batch Contribution:{" "}
                <span className="muted">
                  {formatEther(batch.totalContribution)} / 1000 credits
                </span>
              </small>
              {batch.isComplete && !batch.isClaimed && (
                <small className="font-mono text-uppercase d-block d-flex align-items-center mb-2">
                  Expected Tokens:{" "}
                  <span className="muted">{formatEther(batch.share)}</span>
                </small>
              )}
              <small className="font-mono text-uppercase d-block d-flex align-items-center mb-3">
                Status:{" "}
                <span
                  className={
                    batch.isMinted ||
                    batchPhases[batch.batchId] === "claim-phase"
                      ? "muted text-cyan"
                      : batch.isComplete
                      ? "text-cyan"
                      : "text-yellow"
                  }
                >
                  {batch.isMinted ||
                  batchPhases[batch.batchId] === "claim-phase"
                    ? "Minted"
                    : batch.isComplete
                    ? "Ready to Mint"
                    : "Incomplete"}
                </span>
              </small>
              {((!batch.isMinted && batch.isComplete) ||
                batchPhases[batch.batchId] === "mint-phase") &&
                batchPhases[batch.batchId] !== "claim-phase" &&
                batchPhases[batch.batchId] !== "claimed" && (
                  <button
                    onClick={() => handleCollectiveMint(batch.batchId)}
                    disabled={
                      loadingStates[batch.batchId] ||
                      isMintPending ||
                      isMintConfirming ||
                      batch.batchId === mintingBatchId
                    }
                    className="button mb-2 w-full"
                    style={{ maxWidth: "300px" }}
                  >
                    {(isMintPending || isMintConfirming) &&
                    batch.batchId === mintingBatchId ? (
                      <SvgSpinner />
                    ) : (
                      "Mint Batch"
                    )}
                  </button>
                )}

              {((batch.isMinted && !batch.isClaimed) ||
                batchPhases[batch.batchId] === "claim-phase") &&
                batchPhases[batch.batchId] !== "claimed" && (
                  <button
                    onClick={() => handleClaimTokens(batch.batchId)}
                    disabled={
                      loadingStates[batch.batchId] ||
                      isClaimPending ||
                      isClaimConfirming ||
                      batch.batchId === claimingBatchId
                    }
                    className="button w-full"
                    style={{ maxWidth: "300px" }}
                  >
                    {(isClaimPending || isClaimConfirming) &&
                    batch.batchId === claimingBatchId ? (
                      <SvgSpinner />
                    ) : (
                      "Claim Tokens"
                    )}
                  </button>
                )}

              {(batch.isClaimed ||
                batchPhases[batch.batchId] === "claimed" ||
                (isClaimConfirmed && batch.batchId === claimingBatchId)) && (
                <small
                  className="font-mono text-uppercase d-block d-flex align-items-center"
                  style={{ color: "lime" }}
                >
                  Tokens Claimed ✓
                </small>
              )}
            </div>
          </div>
        ))}
        <small className="font-mono text-uppercase d-block d-flex align-items-center mb-4">
          <span className="muted d-flex align-items-center">
            NOTE: A dedicated Brain DAO contract and interface will be going
            live soon. The Brain DAO contract grants brain token holders the
            ability to collectively manage their brains and brain metadata.
          </span>
        </small>
      </div>
    </>
  );
};
